import { TeamInfo } from './types';

// Logo filenames follow the same convention as the other leagues:
// {ABBR}{first character of nickname}.png — e.g. ARIC = Arizona Cardinals, SF4 = San Francisco 49ers.
// All 32 files verified to exist in blob storage.
const TEAM_LOGO_BASE = 'https://awesemostorage.blob.core.windows.net/sports/nfl/teams';

export const nflTeams: Record<string, TeamInfo> = {
  ARI: { name: 'Cardinals', fullName: 'Arizona Cardinals', abbr: 'ARI', logo: `${TEAM_LOGO_BASE}/ARIC.png` },
  ATL: { name: 'Falcons', fullName: 'Atlanta Falcons', abbr: 'ATL', logo: `${TEAM_LOGO_BASE}/ATLF.png` },
  BAL: { name: 'Ravens', fullName: 'Baltimore Ravens', abbr: 'BAL', logo: `${TEAM_LOGO_BASE}/BALR.png` },
  BUF: { name: 'Bills', fullName: 'Buffalo Bills', abbr: 'BUF', logo: `${TEAM_LOGO_BASE}/BUFB.png` },
  CAR: { name: 'Panthers', fullName: 'Carolina Panthers', abbr: 'CAR', logo: `${TEAM_LOGO_BASE}/CARP.png` },
  CHI: { name: 'Bears', fullName: 'Chicago Bears', abbr: 'CHI', logo: `${TEAM_LOGO_BASE}/CHIB.png` },
  CIN: { name: 'Bengals', fullName: 'Cincinnati Bengals', abbr: 'CIN', logo: `${TEAM_LOGO_BASE}/CINB.png` },
  CLE: { name: 'Browns', fullName: 'Cleveland Browns', abbr: 'CLE', logo: `${TEAM_LOGO_BASE}/CLEB.png` },
  DAL: { name: 'Cowboys', fullName: 'Dallas Cowboys', abbr: 'DAL', logo: `${TEAM_LOGO_BASE}/DALC.png` },
  DEN: { name: 'Broncos', fullName: 'Denver Broncos', abbr: 'DEN', logo: `${TEAM_LOGO_BASE}/DENB.png` },
  DET: { name: 'Lions', fullName: 'Detroit Lions', abbr: 'DET', logo: `${TEAM_LOGO_BASE}/DETL.png` },
  GB:  { name: 'Packers', fullName: 'Green Bay Packers', abbr: 'GB', logo: `${TEAM_LOGO_BASE}/GBP.png` },
  HOU: { name: 'Texans', fullName: 'Houston Texans', abbr: 'HOU', logo: `${TEAM_LOGO_BASE}/HOUT.png` },
  IND: { name: 'Colts', fullName: 'Indianapolis Colts', abbr: 'IND', logo: `${TEAM_LOGO_BASE}/INDC.png` },
  JAX: { name: 'Jaguars', fullName: 'Jacksonville Jaguars', abbr: 'JAX', logo: `${TEAM_LOGO_BASE}/JAXJ.png` },
  KC:  { name: 'Chiefs', fullName: 'Kansas City Chiefs', abbr: 'KC', logo: `${TEAM_LOGO_BASE}/KCC.png` },
  LAC: { name: 'Chargers', fullName: 'Los Angeles Chargers', abbr: 'LAC', logo: `${TEAM_LOGO_BASE}/LACC.png` },
  LAR: { name: 'Rams', fullName: 'Los Angeles Rams', abbr: 'LAR', logo: `${TEAM_LOGO_BASE}/LARR.png` },
  LV:  { name: 'Raiders', fullName: 'Las Vegas Raiders', abbr: 'LV', logo: `${TEAM_LOGO_BASE}/LVR.png` },
  MIA: { name: 'Dolphins', fullName: 'Miami Dolphins', abbr: 'MIA', logo: `${TEAM_LOGO_BASE}/MIAD.png` },
  MIN: { name: 'Vikings', fullName: 'Minnesota Vikings', abbr: 'MIN', logo: `${TEAM_LOGO_BASE}/MINV.png` },
  NE:  { name: 'Patriots', fullName: 'New England Patriots', abbr: 'NE', logo: `${TEAM_LOGO_BASE}/NEP.png` },
  NO:  { name: 'Saints', fullName: 'New Orleans Saints', abbr: 'NO', logo: `${TEAM_LOGO_BASE}/NOS.png` },
  NYG: { name: 'Giants', fullName: 'New York Giants', abbr: 'NYG', logo: `${TEAM_LOGO_BASE}/NYGG.png` },
  NYJ: { name: 'Jets', fullName: 'New York Jets', abbr: 'NYJ', logo: `${TEAM_LOGO_BASE}/NYJJ.png` },
  PHI: { name: 'Eagles', fullName: 'Philadelphia Eagles', abbr: 'PHI', logo: `${TEAM_LOGO_BASE}/PHIE.png` },
  PIT: { name: 'Steelers', fullName: 'Pittsburgh Steelers', abbr: 'PIT', logo: `${TEAM_LOGO_BASE}/PITS.png` },
  SF:  { name: '49ers', fullName: 'San Francisco 49ers', abbr: 'SF', logo: `${TEAM_LOGO_BASE}/SF4.png` },
  SEA: { name: 'Seahawks', fullName: 'Seattle Seahawks', abbr: 'SEA', logo: `${TEAM_LOGO_BASE}/SEAS.png` },
  TB:  { name: 'Buccaneers', fullName: 'Tampa Bay Buccaneers', abbr: 'TB', logo: `${TEAM_LOGO_BASE}/TBB.png` },
  TEN: { name: 'Titans', fullName: 'Tennessee Titans', abbr: 'TEN', logo: `${TEAM_LOGO_BASE}/TENT.png` },
  WAS: { name: 'Commanders', fullName: 'Washington Commanders', abbr: 'WAS', logo: `${TEAM_LOGO_BASE}/WASC.png` },
};

/**
 * Alternate abbreviations seen across odds/projection sources, mapped to the
 * canonical keys above. Applied only when the active league is NFL so they
 * don't collide with the NBA/NHL variants (e.g. NHL "WAS" -> "WSH").
 */
export const NFL_TEAM_ABBR_NORMALIZE: Record<string, string> = {
  WSH: 'WAS',
  JAC: 'JAX',
  GNB: 'GB',
  KAN: 'KC',
  SFO: 'SF',
  TAM: 'TB',
  NWE: 'NE',
  NOR: 'NO',
  LA: 'LAR',
  STL: 'LAR',
  SD: 'LAC',
  SDG: 'LAC',
  OAK: 'LV',
  LVR: 'LV',
};

export function getNFLTeamInfo(abbr: string): TeamInfo {
  const upper = abbr.toUpperCase();
  return nflTeams[NFL_TEAM_ABBR_NORMALIZE[upper] || upper] || {
    name: abbr,
    fullName: abbr,
    abbr: abbr,
    logo: '',
  };
}

// Full names and nicknames -> abbreviation, derived from nflTeams plus common alternates.
export const NFL_TEAM_NAME_TO_ABBR: Record<string, string> = {
  ...Object.values(nflTeams).reduce<Record<string, string>>((acc, team) => {
    acc[team.fullName] = team.abbr;
    acc[team.name] = team.abbr;
    return acc;
  }, {}),
  'LA Rams': 'LAR',
  'LA Chargers': 'LAC',
  'NY Giants': 'NYG',
  'NY Jets': 'NYJ',
  'Washington Football Team': 'WAS',
  'Washington Redskins': 'WAS',
  'Oakland Raiders': 'LV',
  'San Diego Chargers': 'LAC',
  'St. Louis Rams': 'LAR',
};
